import { type JSX } from 'react';
import { AlertTriangle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { PartyName } from './ui/index.ts';

/**
 * Past-due warning for ONE practice, shown app-wide to its members. Access is still allowed
 * (the server lets past_due through with an X-Subscription-Warning header while Stripe
 * retries the card); this is a non-blocking warning, distinct from the cancelled/suspended
 * interstitial.
 */
export function SubscriptionBanner({ practiceId, practiceName, linkTo }: { practiceId: string; practiceName: string; linkTo?: string }): JSX.Element {
  return (
    <div
      role="status"
      data-testid="subscription-warning-banner"
      className="flex flex-wrap items-center justify-between gap-3 border-b border-destructive/30 bg-destructive/10 px-4 py-2 text-sm text-foreground"
    >
      <div className="flex items-center gap-2">
        <AlertTriangle className="h-4 w-4 shrink-0 text-destructive" aria-hidden="true" />
        <span>
          A payment for <PartyName>{practiceName}</PartyName>&rsquo;s plan failed. Update the card to avoid interruption.
        </span>
      </div>
      <Link
        to={linkTo ?? `/practices/${practiceId}/plan`}
        className="text-sm font-medium underline underline-offset-2"
        data-testid="subscription-warning-banner-link"
      >
        Plan
      </Link>
    </div>
  );
}

/** The practice is plan-only: cancelled/suspended, or 14+ days owing (ToS 9.5, terms-2.4). Its
 *  own routes show the interstitial; this is the shell-wide banner for everywhere else. */
export function SubscriptionEndedBanner({
  practiceId,
  practiceName,
  status,
  linkTo,
}: {
  practiceId: string;
  practiceName: string;
  /** 'pilot'/'trialing' here means the ladder triggered it, not the Stripe status itself. */
  status: 'cancelled' | 'suspended' | 'pilot' | 'trialing';
  linkTo?: string;
}): JSX.Element {
  const clause =
    status === 'suspended' ? ' is suspended'
      : status === 'cancelled' ? '\u2019s plan has ended'
        : '\u2019s free period has ended';
  return (
    <div
      role="status"
      data-testid="subscription-ended-banner"
      className="flex flex-wrap items-center justify-between gap-3 border-b border-destructive/30 bg-destructive/10 px-4 py-2 text-sm text-foreground"
    >
      <div className="flex items-center gap-2">
        <AlertTriangle className="h-4 w-4 shrink-0 text-destructive" aria-hidden="true" />
        <span>
          <PartyName>{practiceName}</PartyName>{clause}: its
          members can reach only Plan until a plan is in place.
        </span>
      </div>
      <Link
        to={linkTo ?? `/practices/${practiceId}/plan`}
        className="text-sm font-medium underline underline-offset-2"
        data-testid="subscription-ended-banner-link"
      >
        Plan
      </Link>
    </div>
  );
}

/** 7-13 days owing (ToS 9.5, terms-2.4): reads, export, the plan page and recording a payment
 *  against an existing invoice stay open; nothing new can be added. Non-blocking, unlike the
 *  plan-only interstitial \u2014 most of the practice still works. */
export function SubscriptionReadOnlyBanner({
  practiceId,
  practiceName,
  linkTo,
}: {
  practiceId: string;
  practiceName: string;
  linkTo?: string;
}): JSX.Element {
  return (
    <div
      role="status"
      data-testid="subscription-read-only-banner"
      className="flex flex-wrap items-center justify-between gap-3 border-b border-destructive/30 bg-destructive/10 px-4 py-2 text-sm text-foreground"
    >
      <div className="flex items-center gap-2">
        <AlertTriangle className="h-4 w-4 shrink-0 text-destructive" aria-hidden="true" />
        <span>
          <PartyName>{practiceName}</PartyName>&rsquo;s free period ended without a plan: it is now read-only.
          Records stay readable and exportable, but nothing new can be added.
        </span>
      </div>
      <Link
        to={linkTo ?? `/practices/${practiceId}/plan`}
        className="text-sm font-medium underline underline-offset-2"
        data-testid="subscription-read-only-banner-link"
      >
        Plan
      </Link>
    </div>
  );
}
